import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BreadcrumbProps } from '@blueprintjs/core'
import { axiosGet, axiosPost, axiosDestroy } from '../util/api'
import { money, number } from '../util/formatters'
import { Plan } from '../types/billing'
import { useUser } from '../hooks/useUser'
import { refreshUser } from '../actions/user'
import { BillingInfoCallout } from '../components/billings/BillingInfoCallout'
import { Link, useNavigate } from 'react-router-dom'
import { fetchCards } from '../actions/cards'
import { AdSpendSlider } from './billing/ad_spend_slider'
import { Marketplace, Marketplaces } from '../util/marketplaces'
import { Layout } from '../components/Layout'
import { Toasts } from '../components/toaster/Toast'
import { Tooltip } from '../components/ui/tooltip'
import { State } from '../reducers'
import { SalesBasedBilling } from '../components/billing_sales/SalesBasedBilling'
import { SpendBasedBilling } from '../components/billing_spend_new/SpendBasedBilling'
import { AnchorButton, Button } from '../components/ui/button'
export const Billing = () => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(true)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [plan, setPlan] = useState<Plan | null>(null)
    const [subscribing, setSubscribing] = useState(false)
    const [adSpend, setAdSpend] = useState<number>(500)
    const [adSpendLoading, setAdSpendLoading] = useState(false)
    const userHasCard = useSelector<State, boolean>((store) => store.cards.cards.length > 0)
    const navigate = useNavigate()

    const user = useUser()
    const activeSubscription = user.activeSubscription

    const loadPlans = (params: any) => {
        return axiosGet('billing/plans', params)
    }

    const loadAdSpend = () => {
        setAdSpendLoading(true)
        axiosGet('billing/ad-spend')
            .then((res) => {
                setAdSpendLoading(false)
                setAdSpend(res.data.spend_usd || 500)
            })
            .catch(() => {
                setAdSpendLoading(false)
            })
    }

    useEffect(() => {
        loadPlans({}).then((res) => {
            setPlan(new Plan(res.data))
            setLoading(false)
        })
        dispatch(fetchCards())

        loadAdSpend()
    }, [])

    const currentCost = useMemo(() => {
        let currentCost = null

        if (plan) {
            currentCost = plan.calculate(adSpend)
        }

        return currentCost
    }, [plan, adSpend])

    if (!plan) {
        return null
    }

    const subscribe = () => {
        setSubscribing(true)
        if (!!activeSubscription && activeSubscription.subscription_id) {
        } else {
            axiosPost('billing/subscription', {
                plan: plan.id,
            })
                .then(() => {
                    setSubscribing(false)
                    Toasts.success('Subscription created')
                    /** @ts-ignore */
                    window.rewardful('convert', { email: user?.user?.email })
                    dispatch(refreshUser())
                })
                .catch(() => {
                    setSubscribing(false)
                })
        }
    }

    const cancelSubscription = () => {
        if (!activeSubscription) {
            return
        }

        if (confirm('Are you sure you want to cancel your subscription?')) {
            setCancelLoading(true)
            axiosDestroy(`billing/subscription/${activeSubscription.subscription_id}/cancel`, {
                subscription: activeSubscription.subscription_id,
            })
                .then(() => {
                    setCancelLoading(false)
                    Toasts.success('Subscription canceled')
                    dispatch(refreshUser())
                    loadPlans({}).then(() => {})
                })
                .catch(() => {
                    setCancelLoading(false)
                })
        }
    }

    const resumeSubscription = () => {
        if (!activeSubscription) {
            return
        }

        setCancelLoading(true)
        axiosPost(`billing/subscription/${activeSubscription.subscription_id}/resume`, {
            subscription: activeSubscription.subscription_id,
        })
            .then(() => {
                setCancelLoading(false)
                Toasts.success('Subscription resumed')
                dispatch(refreshUser())
                loadPlans({}).then(() => {})
            })
            .catch(() => {
                setCancelLoading(false)
            })
    }

    const cancelNowSubscription = () => {
        if (!activeSubscription) {
            return
        }

        if (confirm('Are you sure you want to immediately cancel your subscription?')) {
            setCancelLoading(true)
            axiosDestroy(`billing/subscription/${activeSubscription.subscription_id}/cancel-now`, {
                subscription: activeSubscription.subscription_id,
            })
                .then(() => {
                    setCancelLoading(false)
                    Toasts.success('Subscription canceled')
                    dispatch(refreshUser())
                    loadPlans({}).then(() => {})
                })
                .catch(() => {
                    setCancelLoading(false)
                })
        }
    }

    const goToStripePortal = () => {
        axiosPost('stripe/portal').then((res) => {
            window.open(res.data.redirect_url, '_blank')
        })
    }

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Settings' }]

    const usd = (value: number | undefined) => {
        return money(value ?? null, Marketplaces[Marketplace.UnitedStates])
    }

    const salesBasedBillingPlanIds = [5, 6, 7]
    const newSpendBasedBillingPlanIds = [8, 9, 10]
    const hasSalesBasedBillingPlan = salesBasedBillingPlanIds.includes(
        activeSubscription?.plan_type ?? -1,
    )
    const eligibleForSalesBasedBillingPlan = activeSubscription?.plan_type
        ? salesBasedBillingPlanIds.includes(activeSubscription?.plan_type ?? -1)
        : ['essentials_v1', 'standard_v1', 'premium_v1'].includes(plan?.id)
    const hasNewSpendBasedBillingPlan = newSpendBasedBillingPlanIds.includes(
        activeSubscription?.plan_type ?? -1,
    )
    const eligibleForNewSpendBasedBillingPlan = activeSubscription?.plan_type
        ? newSpendBasedBillingPlanIds.includes(activeSubscription?.plan_type ?? -1)
        : ['essentials_v2', 'standard_v2', 'premium_v2'].includes(plan?.id)

    return (
        <Layout breadcrumbs={breadcrumbs} primary="Billing">
            <div className="max-w-3xl mx-auto">
                {!loading &&
                    eligibleForNewSpendBasedBillingPlan &&
                    !activeSubscription?.subscription_id && <SpendBasedBilling />}
                {!loading &&
                    eligibleForSalesBasedBillingPlan &&
                    !activeSubscription?.subscription_id && <SalesBasedBilling />}
                <div className="flex flex-col items-center">
                    {!loading && (
                        <>
                            {!eligibleForSalesBasedBillingPlan &&
                                !eligibleForNewSpendBasedBillingPlan && (
                                    <span className="font-bold">{plan.name}</span>
                                )}

                            <div className="flex justify-between my-8 w-full">
                                {!eligibleForSalesBasedBillingPlan &&
                                    !eligibleForNewSpendBasedBillingPlan && (
                                        <div className="flex items-center">
                                            <img
                                                src="/coin-jar.png"
                                                style={{ width: 60, height: 60 }}
                                                width={60}
                                                height={60}
                                                alt="Coin Jar"
                                            />
                                            <div className="flex flex-col items-start ml-3">
                                                <div className="text-xl">Coin Jar</div>
                                                <div>
                                                    Current Balance:
                                                    <span className="font-normal ml-2">
                                                        {number(user?.user?.ad_spend_credits)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                {eligibleForSalesBasedBillingPlan && <div />}

                                {((!eligibleForNewSpendBasedBillingPlan &&
                                    !eligibleForSalesBasedBillingPlan) ||
                                    !!activeSubscription?.plan_type) && (
                                    <div className="flex flex-col items-end">
                                        <Button
                                            text="Manage Cards"
                                            className="mb-2"
                                            onClick={() => navigate('/billing/cards')}
                                        />
                                        <Tooltip
                                            position="top"
                                            content={
                                                'View invoices, change billing address, change tax info (VAT number, etc)'
                                            }
                                            openOnTargetFocus={false}
                                        >
                                            <Button
                                                text="Billing Portal"
                                                onClick={goToStripePortal}
                                            />
                                        </Tooltip>
                                    </div>
                                )}
                            </div>

                            <div className="flex w-full justify-around pt-5">
                                <div className="px-4 text-center">
                                    <div>
                                        {!eligibleForNewSpendBasedBillingPlan &&
                                            !eligibleForSalesBasedBillingPlan && (
                                                <AdSpendSlider
                                                    value={adSpend}
                                                    onChange={setAdSpend}
                                                    disabled={adSpendLoading || subscribing}
                                                    loading={adSpendLoading}
                                                    onAdSpendRefresh={loadAdSpend}
                                                />
                                            )}

                                        {!eligibleForNewSpendBasedBillingPlan &&
                                            !eligibleForSalesBasedBillingPlan && (
                                                <div className="mt-6 flex flex-col">
                                                    {!activeSubscription?.subscription_id && (
                                                        <div className="mt-3">
                                                            <Tooltip
                                                                content={
                                                                    <div className="flex flex-col">
                                                                        <div>
                                                                            You must add a card to
                                                                            your account before you
                                                                            can create or change a
                                                                            plan
                                                                        </div>
                                                                        <div className="flex justify-center mt-2">
                                                                            <Button
                                                                                text="Add Card"
                                                                                onClick={() =>
                                                                                    navigate(
                                                                                        '/billing/cards',
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                }
                                                                disabled={userHasCard}
                                                                interactionKind="hover"
                                                                hoverCloseDelay={200}
                                                            >
                                                                <AnchorButton
                                                                    disabled={!userHasCard}
                                                                    loading={subscribing}
                                                                    intent="success"
                                                                    text="Subscribe"
                                                                    large
                                                                    onClick={subscribe}
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                    )}

                                                    <div
                                                        className={
                                                            'mt-4 p-2 border border-gra-300 flex flex-col ' +
                                                            'rounded items-start justify-start content-start'
                                                        }
                                                    >
                                                        <div className="inline">
                                                            <span>Estimated Total: </span>
                                                            <b>
                                                                {usd(currentCost?.total_cost)} per
                                                                month
                                                            </b>
                                                        </div>
                                                        <ul className="list-disc ml-4 flex flex-col items-start justify-start">
                                                            <li>
                                                                {usd(currentCost?.base_cost)} base
                                                                price
                                                            </li>
                                                            <Tooltip
                                                                content={
                                                                    <div
                                                                        style={{
                                                                            maxWidth: '400px',
                                                                        }}
                                                                    >
                                                                        This assumes your monthly ad
                                                                        spend stays consistent. The
                                                                        ad spend portion of your
                                                                        bill is based on a graduated
                                                                        percentage of ad spend and
                                                                        is billed in $100
                                                                        increments.
                                                                    </div>
                                                                }
                                                            >
                                                                <li>
                                                                    <div className="has-tooltip">
                                                                        +{' '}
                                                                        {usd(
                                                                            currentCost?.ad_spend_cost,
                                                                        )}{' '}
                                                                        in estimated ad spend costs
                                                                    </div>
                                                                </li>
                                                            </Tooltip>
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        {!!activeSubscription && (
                                            <div className={'mt-5 w-100 text-left'}>
                                                <BillingInfoCallout
                                                    onCancel={cancelSubscription}
                                                    onCancelNow={cancelNowSubscription}
                                                    onResume={resumeSubscription}
                                                    subscription={activeSubscription}
                                                    btnLoading={cancelLoading}
                                                    hasSalesBasedBillingPlan={
                                                        hasSalesBasedBillingPlan
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    )
}
